import React from "react";
import { Box, Grid, Stack } from "@mui/joy";
import type { ZekayCustomer } from "../../../../../api/customers/Customer.typed";
import { SMissing } from "../../../../../styles/StyledMUI";
import { formatBirthdate } from "../../../../../utils/dateTime";
import DarkTypography from "../../../../common/DarkTypography";
import PhotoComponent from "./PhotoComponent";
import ProfileFormSection from "./ProfileFormSection";
import {
  emojiFontFamily,
  profileFormIdentityPhotoColumnSx,
} from "../../../../../styles/globalStyles";
import { normalizeCustomerEmoji } from "../../../../common/customerEmoji";

type ProfileFormViewProps = {
  customer: ZekayCustomer;
  canEditProfile: boolean;
  setCustomerUpdated: React.Dispatch<React.SetStateAction<ZekayCustomer | undefined>>;
};

const rowSx = { fontSize: { xs: "0.875rem", sm: "1rem" } } as const;

export default function ProfileFormView({
  customer,
  canEditProfile,
  setCustomerUpdated,
}: ProfileFormViewProps) {
  const customerEmoji = normalizeCustomerEmoji(customer?.Emoji);

  const missingRow = (text: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <SMissing />
      <DarkTypography colorVariant="primary" sx={rowSx}>
        {text}
      </DarkTypography>
    </Box>
  );

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ maxWidth: "100%" }}>
      <ProfileFormSection title="Identité">
        <Grid container spacing={{ xs: 2, sm: 2.5 }} alignItems="flex-start">
          <Grid xs={12} md={8} sx={{ minWidth: 0 }}>
            <DarkTypography
              level="title-md"
              colorVariant="title"
              sx={{ mb: { xs: 1.25, sm: 1.5 }, wordBreak: "break-word" }}
            >
              {`${customer?.FirstName ?? ""} ${customer?.LastName ?? ""}`.trim() || "—"}
              {customerEmoji ? (
                <Box component="span" sx={{ ml: 0.75, fontFamily: emojiFontFamily }} aria-hidden>
                  {customerEmoji}
                </Box>
              ) : null}
            </DarkTypography>
            <DarkTypography
              level="body-sm"
              colorVariant="secondary"
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              Date de naissance
            </DarkTypography>
            {customer?.Birthdate && formatBirthdate(customer.Birthdate) ? (
              <DarkTypography colorVariant="primary" sx={rowSx}>
                {formatBirthdate(customer.Birthdate)}
              </DarkTypography>
            ) : (
              missingRow("Date de naissance non renseignée")
            )}
          </Grid>
          <Grid xs={12} md={4} sx={profileFormIdentityPhotoColumnSx}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "min(100%, 220px)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <PhotoComponent
                customer={customer}
                setCustomerUpdated={setCustomerUpdated}
                readOnly={!canEditProfile}
              />
            </Box>
          </Grid>
        </Grid>
      </ProfileFormSection>
      <ProfileFormSection title="Coordonnées">
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid xs={12} sm={6}>
            <DarkTypography
              level="body-sm"
              colorVariant="secondary"
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              Email
            </DarkTypography>
            {customer?.MailAddress ? (
              <DarkTypography colorVariant="primary" sx={rowSx}>
                {customer.MailAddress}
              </DarkTypography>
            ) : (
              missingRow("Email non renseigné")
            )}
          </Grid>
          <Grid xs={12} sm={6}>
            <DarkTypography
              level="body-sm"
              colorVariant="secondary"
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              Téléphone
            </DarkTypography>
            {customer?.Phonenumber ? (
              <DarkTypography colorVariant="primary" sx={rowSx}>
                {customer.Phonenumber}
              </DarkTypography>
            ) : (
              missingRow("Téléphone non renseigné")
            )}
          </Grid>
          <Grid xs={12}>
            <DarkTypography
              level="body-sm"
              colorVariant="secondary"
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              Adresse
            </DarkTypography>
            {customer?.Street || customer?.PostalCode || customer?.City ? (
              <DarkTypography colorVariant="primary" sx={rowSx}>
                {`${customer?.Street || ""} ${customer?.PostalCode || ""} ${customer?.City ?? ""}`.trim()}
              </DarkTypography>
            ) : (
              missingRow("Adresse non renseignée")
            )}
          </Grid>
        </Grid>
      </ProfileFormSection>

      <ProfileFormSection title="Qualification">
        <DarkTypography colorVariant="primary" sx={rowSx}>
          Niveau : {customer?.LastQualification ?? "—"}
        </DarkTypography>
      </ProfileFormSection>

      <ProfileFormSection title="Newsletter">
        <DarkTypography colorVariant="primary" sx={rowSx}>
          Recevoir la newsletter : {customer?.GdprConsent ? "Oui" : "Non"}
        </DarkTypography>
      </ProfileFormSection>

      <ProfileFormSection title="Identifiants">
        <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontStyle: "italic" }}>
          ID : {customer?.Id}
        </DarkTypography>
        <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontStyle: "italic", mt: 0.5 }}>
          ID externe : {customer?.ExternalId}
        </DarkTypography>
      </ProfileFormSection>
    </Stack>
  );
}
